import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageSquare, 
  Send, 
  Plus,
  Sparkles,
  User,
  Trash2
} from 'lucide-react';
import { chatApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const Coach = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const response = await chatApi.getSessions();
      setSessions(response.data || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  }, []);

  const loadChatHistory = useCallback(async (sessionId) => {
    setLoadingHistory(true);
    try {
      const response = await chatApi.getHistory(sessionId);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (currentSessionId) {
      loadChatHistory(currentSessionId);
    }
  }, [currentSessionId, loadChatHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await chatApi.send(input, currentSessionId);
      const { response: aiResponse, session_id } = response.data;
      
      if (!currentSessionId) {
        setCurrentSessionId(session_id);
        loadSessions();
      }

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse,
        created_at: new Date().toISOString()
      }]);
    } catch (error) {
      toast.error('Failed to get response. Please try again.');
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = () => {
    setCurrentSessionId(null);
    setMessages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedPrompts = [
    "Help me create a strategic plan for my startup",
    "How can I improve my team's execution?",
    "What makes a good strategic goal?",
    "Guide me through a SWOT analysis",
  ];

  return (
    <div className="h-[calc(100vh-6rem)] flex gap-6" data-testid="coach-page">
      {/* Sidebar - Chat Sessions */}
      <div className="hidden md:flex flex-col w-64 bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl">
        <div className="p-4 border-b border-slate-200 dark:border-[#1F2937]">
          <Button 
            onClick={startNewSession}
            className="w-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full gap-2"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </Button>
        </div>
        <ScrollArea className="flex-1 p-2">
          {sessions.length > 0 ? (
            <div className="space-y-1">
              {sessions.map((session, index) => (
                <button
                  key={session.session_id}
                  onClick={() => setCurrentSessionId(session.session_id)}
                  className={`w-full text-left p-3 rounded-xl transition-colors ${
                    currentSessionId === session.session_id
                      ? 'bg-[#26C6DA]/20 text-[#26C6DA]'
                      : 'hover:bg-slate-100 dark:bg-[#1A1A2E] text-slate-500 dark:text-slate-400'
                  }`}
                  data-testid={`session-${index}`}
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm truncate">Chat {index + 1}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {session.message_count} messages
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 text-sm py-4">
              No chat history yet
            </p>
          )}
        </ScrollArea>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-[#1F2937] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
              AI Strategy Coach
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">Powered by GPT-5.2</p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 && !loadingHistory ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center mb-6">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                Your Strategic Partner
              </h3>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">
                I'm here to help you with strategic planning, goal setting, execution frameworks, 
                and any business challenges you're facing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                {suggestedPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(prompt)}
                    className="p-4 text-left bg-slate-100 dark:bg-[#1A1A2E] hover:bg-[#1F2937] border border-slate-200 dark:border-[#1F2937] rounded-xl transition-colors text-sm text-slate-600 dark:text-slate-300"
                    data-testid={`suggested-prompt-${index}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : loadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${index}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] p-4 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] text-black rounded-br-sm'
                        : 'bg-slate-100 dark:bg-[#1A1A2E] text-slate-800 dark:text-slate-100 rounded-bl-sm border border-slate-200 dark:border-[#1F2937]'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                  {message.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-[#1F2937] flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-[#26C6DA]" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-slate-100 dark:bg-[#1A1A2E] p-4 rounded-2xl rounded-bl-sm border border-slate-200 dark:border-[#1F2937]">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-[#26C6DA] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-[#26C6DA] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-[#26C6DA] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t border-slate-200 dark:border-[#1F2937]">
          <div className="flex gap-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about strategy, goals, or execution..."
              className="flex-1 min-h-[50px] max-h-[150px] bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:border-[#26C6DA] focus:ring-1 focus:ring-[#26C6DA] rounded-xl resize-none"
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black rounded-xl px-4 self-end"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default Coach;
